import type { Theme } from '../settings/themes.js';

/**
 * Canvas-render palette consumed by every renderer. Derived from the active
 * theme via `themeToRenderPalette`; per-game crown gold + interaction tokens
 * (mandatory pulse) come from the checkers `Theme` extension, while board
 * wood, square gradients, and piece colors flow through from the shared
 * `@bn-games/theming` base palette.
 */
export interface RenderPalette {
  readonly name: string;

  readonly borderWoodBase: string;
  readonly borderWoodDark: string;
  readonly borderWoodLight: string;

  readonly boardWoodBase: string;
  readonly boardWoodDark: string;
  readonly boardWoodLight: string;

  readonly darkSquareBase: string;
  readonly darkSquareDeep: string;
  readonly lightSquareBase: string;
  readonly lightSquareDeep: string;

  readonly labelColor: string;
  readonly innerShadow: string;
  readonly innerHighlight: string;

  readonly whitePieceBase: string;
  readonly whitePieceHighlight: string;
  readonly whitePieceShadow: string;
  readonly whitePieceBorder: string;
  readonly whiteCrownGold: string;
  readonly whiteCrownDeep: string;

  readonly blackPieceBase: string;
  readonly blackPieceHighlight: string;
  readonly blackPieceShadow: string;
  readonly blackPieceBorder: string;
  readonly blackCrownGold: string;
  readonly blackCrownDeep: string;

  readonly hoverGlow: string;
  readonly selectedRing: string;
  readonly validDestination: string;
  readonly mandatoryPulse: string;
  readonly captureTarget: string;

  readonly bannerBg: string;
  readonly bannerWinBg: string;
  readonly bannerDrawBg: string;
  readonly bannerBorder: string;
  readonly bannerTextColor: string;
}

/**
 * Bridges the checkers `Theme` (rooted in `@bn-games/theming`) into the
 * RenderPalette shape every renderer expects. Lighting effects (innerShadow,
 * innerHighlight) and semantic colors (green valid-destination, red capture
 * target) stay constant across themes — they encode game state, not chrome.
 */
export function themeToRenderPalette(theme: Theme): RenderPalette {
  return {
    name: theme.name,

    borderWoodBase: theme.borderWoodBase,
    borderWoodDark: theme.borderWoodDark,
    borderWoodLight: theme.borderWoodLight,

    boardWoodBase: theme.boardWoodBase,
    boardWoodDark: theme.boardWoodDark,
    boardWoodLight: theme.boardWoodLight,

    darkSquareBase: theme.darkSquareBase,
    darkSquareDeep: theme.darkSquareTip,
    lightSquareBase: theme.lightSquareBase,
    lightSquareDeep: theme.lightSquareTip,

    labelColor: theme.inkSoft,
    innerShadow: 'rgba(0, 0, 0, 0.45)',
    innerHighlight: 'rgba(255, 220, 160, 0.18)',

    whitePieceBase: theme.whiteCheckerColor,
    whitePieceHighlight: theme.whiteCheckerHighlight,
    whitePieceShadow: theme.whiteCheckerShadow,
    whitePieceBorder: theme.whiteCheckerBorder,
    whiteCrownGold: theme.kingCrownColor,
    whiteCrownDeep: theme.brassDeep,

    blackPieceBase: theme.blackCheckerColor,
    blackPieceHighlight: theme.blackCheckerHighlight,
    blackPieceShadow: theme.blackCheckerShadow,
    blackPieceBorder: theme.blackCheckerBorder,
    blackCrownGold: theme.kingCrownHighlight,
    blackCrownDeep: theme.brassDeep,

    hoverGlow: theme.hoverGlowColor,
    selectedRing: theme.brassHi,
    validDestination: 'rgba(120, 220, 140, 0.85)',
    mandatoryPulse: theme.mandatoryPulseColor,
    captureTarget: 'rgba(255, 90, 70, 0.85)',

    bannerBg: theme.bannerBg,
    bannerWinBg: theme.bannerWinBg,
    bannerDrawBg: 'rgba(80, 80, 90, 0.9)',
    bannerBorder: theme.bannerBorder,
    bannerTextColor: theme.bannerTextColor,
  };
}
